import { PropTypes } from "prop-types";
import { triggerEvent } from "@/utils/custom-events";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import useScrollLock from "@/hooks/useScrollLock";
import { CSSTransition } from "react-transition-group";
import useCustomEventListener from "@/hooks/useCustomEventListener";
import { cn } from "@/utils/cn";
import { overlay } from "@/store/Overlay";
import useOverlay from "@/hooks/useOverlay";
import "@/css/header.scss";

const headerEl = document.querySelector("header");

const HeaderMenu = ({
  id,
  menus,
  cardImage,
  cardHeadline,
  cardButtonText,
  cardText,
  cardButtonUrl,
  socialsHTML,
  enableGradient,
  enableSearch,
}) => {
  const [open, setOpen] = useState(false);
  const [headerTop, setHeaderTop] = useState("4.5rem");
  const mobileMenuRef = useRef(null);

  // Lock side scrolling.
  useScrollLock(open);

  // Calculate header height including announcement bar for mobile positioning
  useEffect(() => {
    const calculateHeaderTop = () => {
      if (headerEl) {
        const headerRect = headerEl.getBoundingClientRect();
        const announcementBar = document.querySelector(".announcement-bar");
        const announcementBarHeight = announcementBar ? announcementBar.offsetHeight : 0;
        const totalHeight = headerRect.height + announcementBarHeight;
        setHeaderTop(`${totalHeight}px`);
      }
    };

    // Calculate on mount and when menu opens/closes
    calculateHeaderTop();
    
    // Recalculate on resize
    window.addEventListener("resize", calculateHeaderTop);
    
    // Also recalculate when menu state changes to account for any layout shifts
    const timeoutId = setTimeout(calculateHeaderTop, 0);
    
    return () => {
      window.removeEventListener("resize", calculateHeaderTop);
      clearTimeout(timeoutId);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      headerEl.classList.add(`header-menu-${id}`);
      headerEl.classList.add("header-menu-open");
    } else {
      headerEl.classList.remove(`header-menu-${id}`);
      headerEl.classList.remove("header-menu-open");
    }
  }, [open, id]);

  function handleToggle(detail) {
    const isOpen = detail?.open;

    setOpen(isOpen === undefined ? !open : isOpen);
  }

  function closeMenu() {
    triggerEvent(`${id}:toggle`, {
      open: false,
    });
  }

  overlay.setOnMouseOver(closeMenu);

  useOverlay(open, false);

  useCustomEventListener(`${id}:toggle`, handleToggle);
  useCustomEventListener("modals:close", () => setOpen(false));

  return (
    <>
      <CSSTransition
        nodeRef={mobileMenuRef}
        in={open}
        timeout={400}
        mountOnEnter
        unmountOnExit
        classNames="fade"
      >
        <div className="flex w-full justify-center">
          <div className="relative flex w-full max-w-[90rem]">
            <div 
              className="header-menu-container fixed left-0 right-0 bottom-0 z-belowHeader md:absolute md:top-0 md:left-0 md:right-0 md:bottom-0 md:z-auto h-full"
              style={{ '--mobile-menu-top': headerTop }}
            >
              <div
                id="header-menu"
                ref={mobileMenuRef}
                className={`header-menu-modal w-full h-full mx-auto overflow-hidden bg-transparent text-deep-green md:max-h-[calc(100vh-122px)] md:max-w-[94.5%] md:h-auto md:overflow-hidden md:bg-gray md:rounded-b-3xl`}
              >
                <div className="flex flex-col h-full md:h-auto">
                  <div className="flex-shrink-0">
                    <TopMenu enableSearch={enableSearch} />
                  </div>
                  <div className="flex-1 overflow-y-auto md:overflow-visible [-webkit-overflow-scrolling:touch] [overscroll-behavior:none] [touch-action:pan-y] bg-gray md:bg-transparent">
                    <div
                      className={`swipeable-content mx-auto max-w-[90rem] bg-transparent md:flex `}
                    >
                      <div
                        className={`w-full mt-0 flex flex-col gap-4 bg-gray px-3 py-4 pb-32 md:mt-0 md:p-7 md:pb-7`}
                      >
                        <div className="flex flex-col gap-5 sm:gap-8 md:gap-8">
                          {/* Category Icons Grid - #dynamic */}
                          {menus?.map((menu) => {
                            if (menu.url?.indexOf("#dynamic") !== -1) {
                              return (
                                <div key={menu.title} className="grid gap-3 grid-cols-2 md:gap-4 md:grid-cols-5">
                                  {menu.children.map(collection => (
                                    <a
                                      key={collection.title}
                                      href={collection.url}
                                      className={cn(
                                        "bg-white rounded-2xl px-3 py-4 md:px-4 md:py-6 relative flex flex-col items-center gap-1.5 md:gap-2 cursor-pointer text-[1.05rem] md:text-[1.2rem] not-italic leading-[120%]"
                                      )}
                                    >
                                      {collection.image && (
                                        <div className="relative h-10 w-10 md:h-12 md:w-12 overflow-hidden rounded-full">
                                          <img
                                            className="h-full w-full object-cover"
                                            src={collection.image}
                                            alt={collection.title}
                                          />
                                        </div>
                                      )}
                                      <h3 className="text-center">{collection.title}</h3>
                                    </a>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })}

                          {/* CTA Button - Using Theme global */}
                          {Theme.header_menu_button_text && Theme.header_menu_button_url && (
                            <div className="flex justify-center m-0 p-0">
                              <a
                                href={Theme.header_menu_button_url}
                                className="button button--secondary w-full sm:w-auto border-gold bg-gold text-deep-green hover:bg-gold hover:text-deep-green hover:opacity-80"
                              >
                                {Theme.header_menu_button_text}
                              </a>
                            </div>
                          )}

                          {/* Secondary Links - #secondary */}
                          {menus?.map((menu) => {
                            if (menu.url?.indexOf("#secondary") !== -1) {
                              return (
                                <div key={menu.title} className="flex flex-col sm:flex-row gap-2 sm:gap-6 justify-start sm:justify-center items-start sm:items-center -mt-1 sm:-mt-3 md:-mt-4">
                                  {menu.children.map(link => (
                                    <a
                                      key={link.title}
                                      href={link.url}
                                      className={cn(
                                        "inline-block cursor-pointer py-2 text-[1rem] not-italic leading-[120%] hover:opacity-70"
                                      )}
                                    >
                                      <h3>{link.title}</h3>
                                    </a>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  );
};

HeaderMenu.propTypes = {
  id: PropTypes.string.isRequired,
  menus: PropTypes.array,
  cardImage: PropTypes.string,
  cardHeadline: PropTypes.string,
  cardText: PropTypes.string,
  cardButtonText: PropTypes.string,
  cardButtonUrl: PropTypes.string,
  socialsHTML: PropTypes.string,
  enableGradient: PropTypes.bool,
  enableSearch: PropTypes.bool,
};

const TopMenu = ({ className, enableSearch = true }) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex size-[14px] h-[52px] w-full items-center justify-between bg-[#104234] px-4 text-[#E7E7E0] md:hidden",
        className,
      )}
    >
      <div>
        {enableSearch && (
          <a href="/search" className="flex items-center gap-2">
            <svg
              className=""
              fill="currentColor"
              stroke="currentColor"
              width="16"
              height="16"
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12.6445 22.6229C15.1433 22.6229 17.422 21.6977 19.1725 20.1803L28.3249 29.3327L29.3327 28.3249L20.1803 19.1725C21.6977 17.422 22.6229 15.1433 22.6229 12.6445C22.6229 7.13351 18.1554 2.66602 12.6445 2.66602C7.13351 2.66602 2.66602 7.13351 2.66602 12.6445C2.66602 18.1554 7.13351 22.6229 12.6445 22.6229ZM12.6445 4.09151C17.3607 4.09151 21.1974 7.92822 21.1974 12.6445C21.1974 17.3607 17.3607 21.1974 12.6445 21.1974C7.92822 21.1974 4.09151 17.3607 4.09151 12.6445C4.09151 7.92822 7.92822 4.09151 12.6445 4.09151Z" />
            </svg>

            {t("action.search")}
          </a>
        )}
      </div>
      <a
        href="https://account.primalharvest.com"
        className="flex items-center gap-2"
      >
        <svg
          className=""
          fill="currentColor"
          stroke="currentColor"
          width="16"
          height="16"
          viewBox="0 0 32 32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M19.3933 16.4973C21.734 15.272 23.334 12.8247 23.334 9.99935C23.334 5.94935 20.0506 2.66602 16.0007 2.66602C11.9507 2.66602 8.66732 5.94935 8.66732 9.99935C8.66732 12.8247 10.2673 15.272 12.608 16.4973C7.26065 18.0593 3.33398 23.2053 3.33398 29.3327H28.6673C28.6673 23.2053 24.7407 18.0593 19.3933 16.4973ZM10.0007 9.99935C10.0007 6.69068 12.692 3.99935 16.0007 3.99935C19.3093 3.99935 22.0007 6.69068 22.0007 9.99935C22.0007 13.308 19.3093 15.9993 16.0007 15.9993C12.692 15.9993 10.0007 13.308 10.0007 9.99935ZM16.0007 17.3327C21.824 17.3327 26.636 22.008 27.2647 27.9993H4.73665C5.36532 22.008 10.1773 17.3327 16.0007 17.3327Z" />
        </svg>

        {t("action.account")}
      </a>
    </div>
  );
};

TopMenu.propTypes = {
  className: PropTypes.string,
  enableSearch: PropTypes.bool,
};

export default HeaderMenu;
